import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { formatDateRangeCompact } from '../../util/dateRangeHelpers';
import { getGroupMinimumDuration } from '../../util/booking';
import { determinePackageType } from '../../util/listingAvailabilityHelpers';
import { getStartOf, addTime, isAfterDate } from '../../util/dates';

// Cart bookingDates.endDate is exclusive (the day AFTER the last rental day), same
// convention DateRangeInput.js bridges for the picker itself. The row's display
// needs the INCLUSIVE last day - same shift BookingDatesForm.js's own
// endDateToPickerDate applies - or a 3-day rental ending Jul 23 would display as
// "through Jul 24".
const toInclusiveDisplayEndDate = (endDate, timeZone) => addTime(getStartOf(endDate, 'day', timeZone), -1, 'days', timeZone);
import { useCartGroupAvailability } from './hooks/useCartGroupAvailability';
import CartDrawerDateRangePicker from './CartDrawerDateRangePicker';
import CartDrawerDateTimePicker from './CartDrawerDateTimePicker';
import OutsideClickHandler from '../OutsideClickHandler/OutsideClickHandler';
import { formatCartDateRangeLabel } from '../CartModal/cartModalHelpers';
// Figma confirms the Start date/End date fields are the SAME "Time picker dropdown"
// component the Receiving Item/Returning Item fields below them use - reusing
// DeliveryReturnSelectField's own field/label classes (not a new input style)
// keeps the two visually identical, as designed.
import fulfillmentCss from '../OrderPanel/FeesInputFields/FulfillmentSelectFields.module.css';
import css from './CartDatesEditor.module.css';

/**
 * Changes the rental dates of ONE cart - a group of items from one store sharing one
 * date range - and is the only place that logic lives. Hosted by the listing page's
 * cart drawer (RAS-2511/2512, where it began as "CartDrawerDateRow"), the store
 * page's cart panel, and the cart page (RAS-2627), which differ only in the trigger
 * they show (`variant`) and where the picker renders (`pickerClassName`).
 *
 * Tapping the trigger reveals a picker (day-range for daily groups, date+time for
 * hourly ones), fetches the group's availability lazily, applies the group's rules
 * (a day blocked for any listing is blocked for all; the strictest minimum duration
 * wins) and, on Apply, moves every item in the group in one atomic write. Old
 * confirmed dates stay displayed until Apply succeeds; opening the picker never
 * mutates anything until then. `isOpen` mirrors the host's own open state - flipping
 * it to false collapses (unmounts) any in-progress picker, discarding unsaved
 * selections.
 */
const CartDatesEditor = ({
  isOpen,
  items,
  timeZone,
  intl,
  onSaveCartItemSelectionsBatch,
  onDatesApplied,
  onEditingChange,
  openRequest,
  // Layout hooks for hosts other than the side carts (the cart page): `className` /
  // `rowClassName` are appended to the shared container/row classes, `pickerClassName`
  // wraps the expanded picker (so a host can hang it as a popover instead of pushing
  // the content below it), and `closeOnOutsideClick` closes an open picker on a click
  // anywhere outside this row - the popover convention.
  className,
  rowClassName,
  pickerClassName,
  closeOnOutsideClick = false,
  // 'fields' (default): the side carts' labelled Start date / End date boxes.
  // 'bar': one slim line - calendar icon, "Sep 17 – Sep 18, 2026", "Change dates" -
  // for hosts where the dates are a header fact rather than a form (the cart page's
  // cards). Same picker, same Apply path; only the trigger differs.
  variant = 'fields',
}) => {
  const [isEditing, setIsEditing] = useState(false);

  // baseItem/packageType/isHourly/listingIds are read by hooks below, so they're
  // computed here (before the bookingDates early-return further down) rather than
  // after it - every hook in this component must run unconditionally on every render,
  // per the Rules of Hooks, and these values don't actually need `baseItem` to be
  // defined to compute safely (an empty `items` just yields `listingIds = []`,
  // `packageType = null`, `isHourly = false`).
  //
  // packageType/isHourly are derived from the booking's own dates (determinePackageType,
  // listingAvailabilityHelpers.js - the same duration >= 1 day => 'daily' rule the
  // listing page's own OrderPanel uses), NOT from the cart item's stored `package`
  // field. `package` is a pricing-TIER identifier (e.g. can be 'threeDaysPrice',
  // 'perHourPrice', etc.) set once at add-to-cart time and never recomputed by a
  // later date edit - checking it against a fixed [PER_HOUR, PER_MINUTE] list here
  // was the bug: it's not a reliable "is this booking day-based or hour-based"
  // signal, the booking's own duration is.
  const baseItem = items[0];
  const packageType = baseItem?.bookingDates
    ? determinePackageType(
      new Date(baseItem.bookingDates.startDate),
      new Date(baseItem.bookingDates.endDate)
    )
    : null;
  const isHourly = packageType === 'hourly';
  const listingIds = items.map(i => i.listingId).filter(Boolean);
  const listingIdsKey = listingIds.join('|');

  const {
    blockedDates,
    checkedDates,
    isLoading: isLoadingAvailability,
    fetchAvailability,
  } = useCartGroupAvailability(listingIds);

  useEffect(() => {
    if (!isOpen) {
      setIsEditing(false);
    }
  }, [isOpen]);

  useEffect(() => {
    onEditingChange?.(isEditing);
    // This component now lives inside the collapsible Order Summary body, so it can
    // unmount (summary collapsed) while mid-edit, not just via the isOpen effect
    // above - without this cleanup, CartDrawer's isEditingDates would stay stuck at
    // true and Checkout would stay disabled even though the picker is gone.
    return () => onEditingChange?.(false);
  }, [isEditing, onEditingChange]);

  // RAS-2506 follow-up: catches up a listing added to the group WHILE the picker is
  // already open (e.g. the user leaves the picker open, navigates to another listing
  // from the same store, adds it to cart, and returns) - handleToggle's own fetch
  // only fires on the open transition itself, so without this a newly-joined listing
  // would silently render as fully available (never fetched, but blockedDates/
  // checkedDates from useCartGroupAvailability only reason about listings it's told
  // about) until the picker is closed and reopened. Primes prevListingIdsRef on every
  // render (not just while editing) so the open transition itself never sees a "new"
  // listingId here and double-fetches on top of handleToggle's own call.
  //
  // Catches the new listing up through whichever is later: the picker's default
  // initial range, or the month the user has actually navigated the calendar to since
  // (visibleMonthRef, reported up by CartDrawerDateRangePicker's onCurrentMonthChange)
  // - a plain ref rather than state, since navigating months alone (no new listing)
  // shouldn't re-trigger this effect.
  const prevListingIdsRef = useRef(listingIds);
  const visibleMonthRef = useRef(null);
  useEffect(() => {
    const newListingIds = listingIds.filter(id => !prevListingIdsRef.current.includes(id));
    if (isEditing && newListingIds.length) {
      const rangeStart = getStartOf(new Date(), 'day', timeZone);
      const defaultRangeEnd = getStartOf(rangeStart, 'month', timeZone, 2, 'months');
      // visibleMonthRef only ever gets set for the day-range picker (reported via
      // CartDrawerDateRangePicker's onCurrentMonthChange) - stays null for hourly
      // groups, correctly falling back to defaultRangeEnd below.
      const visibleMonthEnd = visibleMonthRef.current
        ? getStartOf(visibleMonthRef.current, 'month', timeZone, 1, 'months')
        : null;
      const rangeEnd =
        visibleMonthEnd && isAfterDate(visibleMonthEnd, defaultRangeEnd) ? visibleMonthEnd : defaultRangeEnd;
      fetchAvailability(newListingIds, rangeStart, rangeEnd, timeZone);
    }
    prevListingIdsRef.current = listingIds;
  }, [listingIdsKey, isEditing]);

  const openEditor = () => {
    setIsEditing(true);

    // RAS-2509: lazy - only fetched when the picker actually opens. RAS-2506
    // follow-up: also narrow - the currently-visible month plus one month of
    // buffer, not the entire dayCountAvailableForBooking (365-day) window. Opening
    // the picker used to fire one request per month across the whole window
    // (~13 requests per listing) purely to support isDayBlocked answering
    // synchronously for any day the user might scroll to; CartDrawerDateRangePicker
    // now fetches more lazily via onMonthNavigate as the user actually navigates,
    // mirroring BookingDatesForm.js's own fetchMonthData/handleMonthClick pattern
    // for the listing page's calendar.
    //
    // Also fetched (not gated behind `!isHourly` anymore) for hourly groups - this is
    // Gap A's day-level guard: FieldDateAndTimeInput is inherently single-listing, so
    // a group with 2+ hourly listings otherwise has no cross-listing availability
    // check at all. blockedDates/checkedDates below feed CartDrawerDateTimePicker's
    // new extraBlockedDates prop, blocking a day for the whole group if it's blocked
    // for ANY listing in it - a day-level guard only, not the full time-slot-level
    // union FieldDateAndTimeInput itself doesn't support (documented gap, see that
    // component's own doc comment). Doesn't extend as the user navigates the hourly
    // picker's own calendar further (unlike the day-range picker's onMonthNavigate) -
    // a smaller, known limitation of this partial fix.
    const rangeStart = getStartOf(new Date(), 'day', timeZone);
    const rangeEnd = getStartOf(rangeStart, 'month', timeZone, 2, 'months');
    fetchAvailability(listingIds, rangeStart, rangeEnd, timeZone);

    // The calendar opens on the BOOKING's month (CartDrawerDateRangePicker's initial
    // month is the current start date), which for a booking more than ~2 months out
    // lies past the window above - and an unfetched day renders as blocked
    // (buildIsDayBlocked), so every day the shopper could see was disabled until they
    // paged the calendar (client bug report 2026-09-17, cart page: a January booking
    // opened on an all-grey January). Fetch that month plus a buffer too; months the
    // two windows share are served from the per-listing/month cache, not re-queried.
    const bookingStart = baseItem?.bookingDates?.startDate
      ? new Date(baseItem.bookingDates.startDate)
      : null;
    if (bookingStart && isAfterDate(bookingStart, rangeEnd)) {
      const bookingMonthStart = getStartOf(bookingStart, 'month', timeZone);
      const bookingRangeEnd = getStartOf(bookingMonthStart, 'month', timeZone, 2, 'months');
      fetchAvailability(listingIds, bookingMonthStart, bookingRangeEnd, timeZone);
    }
  };

  // Imperative open from outside the row (the cart page's expired-dates "Update
  // Dates" button, which sits below the items while this row sits above them). Any
  // change to a truthy `openRequest` opens the picker; the caller bumps a counter so
  // a second press after the shopper closed it opens it again. Kept as an effect on
  // the prop (not a ref/handle) so it composes with the isOpen reset above.
  const openRequestRef = useRef(openRequest);
  useEffect(() => {
    if (openRequest && openRequest !== openRequestRef.current && !isEditing) {
      openEditor();
    }
    openRequestRef.current = openRequest;
  }, [openRequest]);

  if (!baseItem?.bookingDates) return null;

  const listings = [...new Map(items.filter(i => i.listing).map(i => [i.listingId, i.listing])).values()];
  const minimumDuration = getGroupMinimumDuration(listings, packageType);

  const handleToggle = () => {
    if (isEditing) {
      setIsEditing(false);
      return;
    }
    openEditor();
  };

  const handleMonthNavigate = monthStart => {
    if (isHourly) return;
    const monthEnd = getStartOf(monthStart, 'month', timeZone, 1, 'months');
    fetchAvailability(listingIds, monthStart, monthEnd, timeZone);
  };

  const handleApply = async newBookingDates => {
    // Cart items store bookingDates as ISO strings, not Date instances - same
    // convention BookingDatesForm.js's addToCartHandler already uses when writing to
    // the cart (moment(...).toISOString()). The picker hands back raw Date objects;
    // writing those straight through is what was producing the API's 400
    // ("a-java.util.Date" validation failure) on publicData.cart.bookingDates.
    const isoBookingDates = {
      startDate: moment(newBookingDates.startDate).toISOString(),
      endDate: moment(newBookingDates.endDate).toISOString(),
    };

    // RAS-2506 follow-up: all N items in the group are written in ONE atomic cart
    // update instead of N sequential per-item writes - see updateCartItemsBatch's own
    // doc comment (cart.duck.js) for why the old Promise.all-of-N-dispatches approach
    // could transiently (and sometimes permanently) narrow the drawer down to just
    // one item mid-Apply. This also means failures reject normally instead of having
    // to be read back from itemOperationErrors afterward.
    await onSaveCartItemSelectionsBatch(
      items.map(item => item.id),
      isoBookingDates
    );

    // RAS-2513: price recomputation is NOT triggered here - CartDrawer.js reactively
    // re-fetches once its own `items` prop actually reflects these new dates (after
    // Redux + onDatesApplied's cartDrawerOverride update propagate back down).
    // Triggering it eagerly from here would race ahead of that and fetch pricing for
    // the OLD dates.
    onDatesApplied?.(isoBookingDates);
    setIsEditing(false);
  };

  const startDate = new Date(baseItem.bookingDates.startDate);
  const endDate = isHourly
    ? new Date(baseItem.bookingDates.endDate)
    : toInclusiveDisplayEndDate(new Date(baseItem.bookingDates.endDate), timeZone);

  // Formatted in the BOOKING's zone, not the browser's - a store-zone midnight viewed
  // from a browser west of the store is still the previous evening locally, and would
  // otherwise read as the day before the one the shopper picked.
  const dateOpts = { dateStyle: 'medium', timeZone };
  const timeOpts = { timeZone };
  const startDateDisplay = isHourly
    ? `${intl.formatDate(startDate, dateOpts)}, ${intl.formatTime(startDate, timeOpts)}`
    : intl.formatDate(startDate, dateOpts);
  const endDateDisplay = isHourly
    ? `${intl.formatDate(endDate, dateOpts)}, ${intl.formatTime(endDate, timeOpts)}`
    : intl.formatDate(endDate, dateOpts);
  // Hourly bookings collapse to a SINGLE summary field, not separate Start/End date
  // boxes - two side-by-side date fields visually reads as a day range, which is
  // exactly the wrong-picker impression this was meant to fix (RAS-2506 follow-up).
  // Same calendar day (the common case) shows one date + both times; a booking that
  // happens to span midnight still shows both full date+times so nothing is lost.
  const isSameCalendarDay =
    intl.formatDate(startDate, dateOpts) === intl.formatDate(endDate, dateOpts);
  const hourlySummaryDisplay = isSameCalendarDay
    ? `${intl.formatDate(startDate, dateOpts)}, ${intl.formatTime(startDate, timeOpts)} – ${intl.formatTime(endDate, timeOpts)}`
    : `${startDateDisplay} – ${endDateDisplay}`;

  const fieldButtonClass = isEditing
    ? `${css.dateSelectButton} ${css.dateSelectButtonEditing}`
    : css.dateSelectButton;

  // An expired group (start day already gone - the cart page's "Expired dates" state)
  // still SHOWS its old dates in the fields, but must not seed the picker with them:
  // every day of that range is outside the bookable window, so the calendar would open
  // on a past month with nothing selectable and a selection that can never Apply.
  // Open it on today's month with no selection instead, like a fresh booking.
  const isExpired = isAfterDate(getStartOf(new Date(), 'day', timeZone), getStartOf(startDate, 'day', timeZone));
  const pickerInitialStart = isExpired ? null : new Date(baseItem.bookingDates.startDate);
  const pickerInitialEnd = isExpired ? null : new Date(baseItem.bookingDates.endDate);

  const fields = isHourly ? (
    <div className={fulfillmentCss.field}>
      <label className={fulfillmentCss.label} htmlFor="cartDatesEditorStartField">
        <FormattedMessage id="CartDatesEditor.dateTimeLabel" defaultMessage="Date" />
      </label>
      <button
        type="button"
        id="cartDatesEditorStartField"
        className={fieldButtonClass}
        onClick={handleToggle}
        aria-expanded={isEditing}
      >
        <span className={css.dateSelectValue}>{hourlySummaryDisplay}</span>
        <i className={isEditing ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
      </button>
    </div>
  ) : (
    <>
      <div className={fulfillmentCss.field}>
        <label className={fulfillmentCss.label} htmlFor="cartDatesEditorStartField">
          <FormattedMessage id="CartDatesEditor.startDateLabel" defaultMessage="Start date" />
        </label>
        <button
          type="button"
          id="cartDatesEditorStartField"
          className={fieldButtonClass}
          onClick={handleToggle}
          aria-expanded={isEditing}
        >
          <span className={css.dateSelectValue}>{startDateDisplay}</span>
          <i className={isEditing ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
        </button>
      </div>
      <div className={fulfillmentCss.field}>
        <label className={fulfillmentCss.label} htmlFor="cartDatesEditorEndField">
          <FormattedMessage id="CartDatesEditor.endDateLabel" defaultMessage="End date" />
        </label>
        <button
          type="button"
          id="cartDatesEditorEndField"
          className={fieldButtonClass}
          onClick={handleToggle}
          aria-expanded={isEditing}
        >
          <span className={css.dateSelectValue}>{endDateDisplay}</span>
          <i className={isEditing ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
        </button>
      </div>
    </>
  );

  // The bar's one-line summary: the same "Sep 17 – Sep 18, 2026" the side carts'
  // headers use (shared formatter, inclusive last day), times appended for hourly.
  const barSummary = isHourly
    ? hourlySummaryDisplay
    : formatCartDateRangeLabel(baseItem.bookingDates, timeZone, intl);

  const bar = (
    <button
      type="button"
      id="cartDatesEditorStartField"
      className={classNames(css.bar, { [css.barEditing]: isEditing })}
      onClick={handleToggle}
      aria-expanded={isEditing}
    >
      <span className={css.barDates}>
        <i className="ri-calendar-line" aria-hidden="true" />
        <span className={css.barDatesText}>{barSummary}</span>
      </span>
      <span className={css.barAction}>
        <FormattedMessage id="CartDatesEditor.changeDates" defaultMessage="Change dates" />
        <i className={isEditing ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} aria-hidden="true" />
      </span>
    </button>
  );

  const picker = !isEditing ? null : isHourly ? (
    <CartDrawerDateTimePicker
      listing={baseItem.listing}
      selectedPackage={baseItem.package}
      initialStartDate={pickerInitialStart}
      initialStartTime={pickerInitialStart ? pickerInitialStart.getTime() : null}
      initialEndDate={pickerInitialEnd}
      initialEndTime={pickerInitialEnd ? pickerInitialEnd.getTime() : null}
      minimumMinutes={minimumDuration?.unit === 'minutes' ? minimumDuration.value : null}
      timeZone={timeZone}
      intl={intl}
      onApply={handleApply}
      onClear={() => { }}
      extraBlockedDates={blockedDates}
    />
  ) : (
    <CartDrawerDateRangePicker
      initialStartDate={pickerInitialStart}
      initialEndDate={pickerInitialEnd}
      blockedDates={blockedDates}
      checkedDates={checkedDates}
      isLoadingAvailability={isLoadingAvailability}
      minimumDays={minimumDuration?.unit === 'days' ? minimumDuration.value : null}
      timeZone={timeZone}
      intl={intl}
      onApply={handleApply}
      onClear={() => { }}
      onMonthNavigate={handleMonthNavigate}
      onCurrentMonthChange={month => {
        visibleMonthRef.current = month;
      }}
    />
  );

  const pickerMaybe = picker && pickerClassName ? <div className={pickerClassName}>{picker}</div> : picker;
  const content =
    variant === 'bar' ? (
      <div className={classNames(css.barRoot, className)}>
        {bar}
        {pickerMaybe}
      </div>
    ) : (
      <div className={classNames(fulfillmentCss.container, className)}>
        {/* Own row class, not FulfillmentSelectFields' .row: that one stacks its fields
            below 1024px, which is every side cart at every viewport - the Receiving/
            Returning dropdowns need the room, two short dates don't (client feedback
            2026-09-16: "stacked is too much room"). */}
        <div className={classNames(css.fieldsRow, rowClassName)}>{fields}</div>
        {pickerMaybe}
      </div>
    );

  // The outside-click listener is only attached while the picker is open (the
  // handler registers document listeners on mount), so a closed row costs nothing.
  return closeOnOutsideClick && isEditing ? (
    <OutsideClickHandler rootClassName={css.outsideClickRoot} onOutsideClick={() => setIsEditing(false)}>
      {content}
    </OutsideClickHandler>
  ) : (
    content
  );
};

export default CartDatesEditor;
